import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import Sidebar from "../components/Sidebar.jsx";
import "./Gallerie.css";

const DEFAULT_ROOM_ID = "gallerie_louvre_01";
const GALLERY_WIDTH = 16;
const GALLERY_LENGTH = 140;
const WALL_HEIGHT = 7;
const WALL_OFFSET = 0.1;
const DEFAULT_FRAME_BORDER = 0.2;
const DEFAULT_IMAGE_MARGIN_H = 0.2;
const DEFAULT_IMAGE_MARGIN_V = 0.2;
const SPORTS_WOMAN_URL = new URL("../objets/sports_woman.glb", import.meta.url).toString();
const SOFA_MODEL_URL = new URL("../objets/yellow sofa 3d model_Clone1.glb", import.meta.url).toString();
const GOLD_BASECOLOR_URL = new URL(
  "../objets/textures/BrushedGold02_MR_2K/BrushedGold02_2K_BaseColor.png",
  import.meta.url
).toString();
const GOLD_ROUGHNESS_URL = new URL(
  "../objets/textures/BrushedGold02_MR_2K/BrushedGold02_2K_Roughness.png",
  import.meta.url
).toString();
const GOLD_METALLIC_URL = new URL(
  "../objets/textures/BrushedGold02_MR_2K/BrushedGold02_2K_Metallic.png",
  import.meta.url
).toString();
const GOLD_NORMAL_URL = new URL(
  "../objets/textures/BrushedGold02_MR_2K/BrushedGold02_2K_Normal.png",
  import.meta.url
).toString();

RectAreaLightUniformsLib.init();

function createParquetTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#7a5632";
  ctx.fillRect(0, 0, 512, 512);

  const tile = 128;
  const plank = 16;
  for (let y = 0; y < 512; y += tile) {
    for (let x = 0; x < 512; x += tile) {
      for (let i = 0; i < tile; i += plank) {
        const shade = Math.random() * 16 - 8;
        ctx.fillStyle = `rgb(${125 + shade}, ${92 + shade}, ${62 + shade})`;
        ctx.fillRect(x + i, y + i, tile - i, plank - 2);
        ctx.fillRect(x + i, y + i, plank - 2, tile - i);
      }
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(6, 30);
  texture.anisotropy = 16;
  return texture;
}

function createWallTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#d6d0c7";
  ctx.fillRect(0, 0, 512, 512);

  const panelH = 86;
  ctx.strokeStyle = "rgba(110, 105, 98, 0.45)";
  ctx.lineWidth = 2;
  for (let y = panelH; y < 512; y += panelH) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(512, y);
    ctx.stroke();
  }

  for (let i = 0; i < 35000; i += 1) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const gray = Math.random() * 35;
    ctx.fillStyle = `rgba(${210 - gray}, ${206 - gray}, ${200 - gray}, 0.12)`;
    ctx.fillRect(x, y, 2, 2);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 3);
  return texture;
}

function createNoiseTexture(size, contrast = 35, base = 128) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const image = ctx.createImageData(size, size);
  for (let i = 0; i < image.data.length; i += 4) {
    const val = Math.max(0, Math.min(255, base + (Math.random() * 2 - 1) * contrast));
    image.data[i] = val;
    image.data[i + 1] = val;
    image.data[i + 2] = val;
    image.data[i + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

function createVideoPlaceholderTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const gradient = ctx.createLinearGradient(0, 0, 512, 512);
  gradient.addColorStop(0, "#2f2f2f");
  gradient.addColorStop(1, "#1a1a1a");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 512, 512);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
  ctx.lineWidth = 6;
  ctx.strokeRect(26, 26, 460, 460);
  ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
  ctx.beginPath();
  ctx.moveTo(210, 170);
  ctx.lineTo(360, 256);
  ctx.lineTo(210, 342);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
  ctx.font = "bold 48px Arial";
  ctx.textAlign = "center";
  ctx.fillText("VIDEO", 256, 420);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

function createMarbleTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#b9aba0";
  ctx.fillRect(0, 0, 512, 512);

  for (let i = 0; i < 40; i += 1) {
    const startX = Math.random() * 512;
    const startY = Math.random() * 512;
    let x = startX;
    let y = startY;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const steps = 16 + Math.floor(Math.random() * 10);
    for (let s = 0; s < steps; s += 1) {
      x += (Math.random() - 0.5) * 40;
      y += (Math.random() - 0.5) * 40;
      ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(230, 225, 218, ${0.25 + Math.random() * 0.35})`;
    ctx.lineWidth = 1 + Math.random() * 2.5;
    ctx.stroke();
  }

  for (let i = 0; i < 12000; i += 1) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const gray = Math.random() * 25;
    ctx.fillStyle = `rgba(${190 + gray}, ${185 + gray}, ${175 + gray}, 0.2)`;
    ctx.fillRect(x, y, 2, 2);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 6);
  return texture;
}

const isVideoUrl = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(url);
};

const isModelUrl = (url) => {
  if (!url) return false;
  return /\.(glb|gltf|fbx|obj|usdz)(\?|#|$)/i.test(url);
};

function createSkylightTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const gradient = ctx.createLinearGradient(0, 0, 0, 512);
  gradient.addColorStop(0, "#cfe6ff");
  gradient.addColorStop(1, "#9fc6f0");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 512, 512);

  ctx.strokeStyle = "rgba(20, 20, 20, 0.65)";
  ctx.lineWidth = 2;
  const step = 64;
  for (let i = step; i < 512; i += step) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, 512);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, i);
    ctx.lineTo(512, i);
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(-2, 6);
  texture.offset.set(1, 0);
  return texture;
}

function createLabelTexture(text) {
  if (!text) return null;
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(12, 12, 16, 0.75)";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
  ctx.lineWidth = 2;
  ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
  ctx.fillStyle = "#f2efe9";
  ctx.font = "28px serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function buildFallbackArtworks() {
  const galleryWidth = 16;
  const galleryLength = 140;
  const slotStart = -galleryLength / 2 + 8;
  const slotEnd = galleryLength / 2 - 8;
  const slotStep = 6;
  const slots = [];
  for (let z = slotStart; z <= slotEnd; z += slotStep) {
    slots.push(z);
  }
  let slotIndex = 0;
  const items = [];
  for (let idx = 0; idx < 40; idx += 1) {
    const width = 1.6 + (idx % 4) * 0.3;
    const height = 1.7 + (idx % 3) * 0.35;
    const isLeft = idx % 2 === 0;
    const wallX = isLeft ? -galleryWidth / 2 + 0.1 : galleryWidth / 2 - 0.1;
    const posZ = slots[slotIndex % slots.length];
    slotIndex += 1;
    const posY = 2.2 + (idx % 4) * 0.2;
    items.push({
      artwork_id: `fallback_${idx}`,
      kind: "painting",
      position: { x: wallX, y: posY, z: posZ },
      rotation: { x: 0, y: isLeft ? Math.PI / 2 : -Math.PI / 2, z: 0 },
      size: { w: width, h: height, d: 0.1 },
      wall_side: isLeft ? "left" : "right"
    });
  }
  return items;
}

export default function Gallerie() {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [hasMoved, setHasMoved] = useState(false);
  const [activeArtwork, setActiveArtwork] = useState(null);
  const [movingArtworkId, setMovingArtworkId] = useState("");
  const [moveDraft, setMoveDraft] = useState(null);
  const [transformMode, setTransformMode] = useState("translate");
  const searchParams = new URLSearchParams(window.location.search);
  const initialView = searchParams.get("view") === "scene" ? "scene" : "manage";
  const initialLayoutId = searchParams.get("layout_id") || "";
  const [viewMode, setViewMode] = useState(initialView);
  const [layouts, setLayouts] = useState([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState(initialLayoutId);
  const [layoutDraft, setLayoutDraft] = useState({ title: "", description: "" });
  const [layoutEdit, setLayoutEdit] = useState({ title: "", description: "" });
  const [artworks, setArtworks] = useState([]);
  const [artworkDraft, setArtworkDraft] = useState(null);
  const [selectedArtworkId, setSelectedArtworkId] = useState("");
  const [loadingArtworks, setLoadingArtworks] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const moveRef = useRef({ x: 0, y: 0 });
  const lookRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const artworkMapRef = useRef(new Map());
  const activeArtworkRef = useRef(null);
  const moveDraftRef = useRef(null);
  const textureLoaderRef = useRef(null);
  const gltfLoaderRef = useRef(null);
  const galleryGroupRef = useRef(null);
  const videoPlaceholderRef = useRef(null);
  const transformControlsRef = useRef(null);
  const transformDraggingRef = useRef(false);
  const transformModeRef = useRef("translate");
  if (!videoPlaceholderRef.current) {
    videoPlaceholderRef.current = createVideoPlaceholderTexture();
  }
  const audioEnabledRef = useRef(false);
  const cameraRef = useRef(null);
  const selectedLayout = layouts.find((layout) => layout.layout_id === selectedLayoutId) || null;
  const isSceneMode = viewMode === "scene";

  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    artworkMapRef.current.forEach((entry) => {
      if (entry.video?.element) {
        entry.video.element.muted = !audioEnabled;
      }
    });
  }, [audioEnabled]);

  useEffect(() => {
    moveDraftRef.current = moveDraft;
  }, [moveDraft]);

  useEffect(() => {
    transformModeRef.current = transformMode;
  }, [transformMode]);

  const syncQuery = (nextLayoutId, nextView) => {
    const params = new URLSearchParams(window.location.search);
    if (nextLayoutId) {
      params.set("layout_id", nextLayoutId);
    } else {
      params.delete("layout_id");
    }
    if (nextView && nextView !== "manage") {
      params.set("view", nextView);
    } else {
      params.delete("view");
    }
    const nextQuery = params.toString();
    const base = window.location.pathname;
    window.history.replaceState({}, "", nextQuery ? `${base}?${nextQuery}` : base);
  };

  const applyArtworkLight = (light, brightness) => {
    if (!light) return;
    const intensity = typeof brightness === "number" ? brightness : 1;
    light.intensity = Math.max(0, intensity);
  };

  const applyFrameColor = (entry, color) => {
    if (!entry?.frameMaterial || !color) return;
    entry.frameMaterial.color.set(color);
    entry.frameMaterial.needsUpdate = true;
  };

  const updateCanvasGeometry = (entry, border, marginH, marginV) => {
    if (!entry?.matMesh || !entry?.imageMesh || !entry?.baseSize) return;
    const frameW = entry.baseSize.w || 1;
    const frameH = entry.baseSize.h || 1;
    const safeBorder = Math.max(0.05, Math.min(border || DEFAULT_FRAME_BORDER, Math.min(frameW, frameH) / 3));
    const matW = Math.max(0.2, frameW - safeBorder * 2);
    const matH = Math.max(0.2, frameH - safeBorder * 2);
    entry.matMesh.geometry.dispose();
    entry.matMesh.geometry = new THREE.PlaneGeometry(matW, matH);
    const safeMarginH = Math.max(0, Math.min(marginH ?? DEFAULT_IMAGE_MARGIN_H, matW / 3));
    const safeMarginV = Math.max(0, Math.min(marginV ?? DEFAULT_IMAGE_MARGIN_V, matH / 3));
    const imageW = Math.max(0.2, matW - safeMarginH * 2);
    const imageH = Math.max(0.2, matH - safeMarginV * 2);
    entry.imageMesh.geometry.dispose();
    entry.imageMesh.geometry = new THREE.PlaneGeometry(imageW, imageH);
    if (entry.light) {
      entry.light.width = matW;
      entry.light.height = matH;
    }
    if (entry.frameParts?.length) {
      const depth = 0.1;
      const topBottomW = frameW;
      const topBottomH = safeBorder;
      const sideW = safeBorder;
      const sideH = Math.max(0.2, frameH - safeBorder * 2);
      entry.frameParts[0].geometry.dispose();
      entry.frameParts[0].geometry = new THREE.BoxGeometry(topBottomW, topBottomH, depth);
      entry.frameParts[1].geometry.dispose();
      entry.frameParts[1].geometry = new THREE.BoxGeometry(topBottomW, topBottomH, depth);
      entry.frameParts[2].geometry.dispose();
      entry.frameParts[2].geometry = new THREE.BoxGeometry(sideW, sideH, depth);
      entry.frameParts[3].geometry.dispose();
      entry.frameParts[3].geometry = new THREE.BoxGeometry(sideW, sideH, depth);
      entry.frameParts[0].position.set(0, (frameH - safeBorder) / 2, 0);
      entry.frameParts[1].position.set(0, -(frameH - safeBorder) / 2, 0);
      entry.frameParts[2].position.set(-(frameW - safeBorder) / 2, 0, 0);
      entry.frameParts[3].position.set((frameW - safeBorder) / 2, 0, 0);
    }
  };

  const applyImageAspect = (entry) => {
    if (!entry?.imageMesh || !entry?.material?.map) return;
    const texture = entry.material.map;
    const imgW = texture.image?.width || 1;
    const imgH = texture.image?.height || 1;
    const aspect = imgW / imgH;
    const imageW = entry.imageMesh.geometry.parameters?.width || 1;
    const imageH = entry.imageMesh.geometry.parameters?.height || 1;
    const planeAspect = imageW / imageH;
    let w = imageW;
    let h = imageH;
    if (aspect >= planeAspect) {
      h = w / aspect;
    } else {
      w = h * aspect;
    }
    entry.imageMesh.scale.set(w / imageW, h / imageH, 1);
  };

  const applyArtworkSizeToMesh = (entry, size) => {
    if (!entry?.mesh || !size) return;
    const base = entry.mesh.userData.baseSize || { w: 1, h: 1, d: 0.1 };
    const w = size.w || base.w;
    const h = size.h || base.h;
    const d = size.d || base.d;
    entry.mesh.scale.set(w / base.w, h / base.h, d / base.d);
  };

  const applyMoveDraftToMesh = (draft) => {
    if (!draft || !draft.artwork_id) return;
    const entry = artworkMapRef.current.get(draft.artwork_id);
    if (!entry || !entry.mesh) return;
    const pos = draft.position || {};
    const rot = draft.rotation || {};
    entry.mesh.position.set(pos.x || 0, pos.y || 0, pos.z || 0);
    entry.mesh.rotation.set(rot.x || 0, rot.y || 0, rot.z || 0);
    if (draft.size) {
      applyArtworkSizeToMesh(entry, draft.size);
    }
    if (typeof draft.brightness === "number") {
      applyArtworkLight(entry.light, draft.brightness);
    }
    if (typeof draft.frame_border === "number" || typeof draft.image_margin_h === "number" || typeof draft.image_margin_v === "number") {
      updateCanvasGeometry(entry, draft.frame_border, draft.image_margin_h, draft.image_margin_v);
      applyImageAspect(entry);
    }
    if (draft.frame_color) {
      applyFrameColor(entry, draft.frame_color);
    }
  };

  const computeAlignedDraft = (artworkId) => {
    const camera = cameraRef.current;
    const entry = artworkMapRef.current.get(artworkId);
    if (!camera || !entry) return null;
    const kind = (entry.artwork?.kind || "").toLowerCase();
    const freeMove = kind && kind !== "painting" ? true : isModelUrl(entry.binding?.asset_url);
    if (freeMove) {
      const pos = entry.mesh.position;
      const rot = entry.mesh.rotation;
      const baseSize = entry.mesh.userData.baseSize || entry.artwork?.size || { w: 2.2, h: 1.6, d: 0.1 };
      return {
        artwork_id: artworkId,
        position: { x: pos.x, y: pos.y, z: pos.z },
        rotation: { x: rot.x, y: rot.y, z: rot.z },
        wall_side: entry.artwork?.wall_side || "",
        size: { ...baseSize },
        brightness: typeof entry.artwork?.brightness === "number" ? entry.artwork.brightness : 1,
        frame_border: typeof entry.artwork?.frame_border === "number" ? entry.artwork.frame_border : DEFAULT_FRAME_BORDER,
        frame_color: entry.artwork?.frame_color || "#b48840",
        image_margin_h: typeof entry.artwork?.image_margin_h === "number" ? entry.artwork.image_margin_h : DEFAULT_IMAGE_MARGIN_H,
        image_margin_v: typeof entry.artwork?.image_margin_v === "number" ? entry.artwork.image_margin_v : DEFAULT_IMAGE_MARGIN_V,
        freeMove: true
      };
    }
    const forward = new THREE.Vector3(0, 0, -1).applyEuler(camera.rotation);
    const wallSide = camera.position.x < 0 ? "left" : "right";
    const wallX = wallSide === "left" ? -GALLERY_WIDTH / 2 + WALL_OFFSET : GALLERY_WIDTH / 2 - WALL_OFFSET;
    const posZ = camera.position.z + forward.z * 2;
    const posY = Math.max(1, Math.min(WALL_HEIGHT - 1, camera.position.y));
    const rotation = {
      x: 0,
      y: wallSide === "left" ? Math.PI / 2 : -Math.PI / 2,
      z: 0
    };
    const baseSize = entry.artwork?.size || { w: 2.2, h: 1.6, d: 0.1 };
    return {
      artwork_id: artworkId,
      position: { x: wallX, y: posY, z: posZ },
      rotation,
      wall_side: wallSide,
      size: { ...baseSize },
      brightness: typeof entry.artwork?.brightness === "number" ? entry.artwork.brightness : 1,
      frame_border: typeof entry.artwork?.frame_border === "number" ? entry.artwork.frame_border : DEFAULT_FRAME_BORDER,
      frame_color: entry.artwork?.frame_color || "#b48840",
      image_margin_h: typeof entry.artwork?.image_margin_h === "number" ? entry.artwork.image_margin_h : DEFAULT_IMAGE_MARGIN_H,
      image_margin_v: typeof entry.artwork?.image_margin_v === "number" ? entry.artwork.image_margin_v : DEFAULT_IMAGE_MARGIN_V,
      freeMove: false
    };
  };

  const startMoveArtwork = () => {
    if (!activeArtwork?.artwork_id) return;
    const draft = computeAlignedDraft(activeArtwork.artwork_id);
    if (!draft) return;
    setMovingArtworkId(activeArtwork.artwork_id);
    setMoveDraft(draft);
    setTransformMode("translate");
    applyMoveDraftToMesh(draft);
  };

  const adjustMoveDraft = (field, axis, delta) => {
    if (!moveDraft) return;
    const next = { ...moveDraft };
    if (field === "position") {
      const pos = { ...(next.position || {}) };
      pos[axis] = (pos[axis] || 0) + delta;
      if (axis === "x" && !next.freeMove) {
        const wallSide = next.wall_side || (pos.x < 0 ? "left" : "right");
        pos.x = wallSide === "left" ? -GALLERY_WIDTH / 2 + WALL_OFFSET : GALLERY_WIDTH / 2 - WALL_OFFSET;
      }
      next.position = pos;
    } else if (field === "rotation") {
      const rot = { ...(next.rotation || {}) };
      rot[axis] = (rot[axis] || 0) + delta;
      next.rotation = rot;
    } else if (field === "size") {
      const size = { ...(next.size || {}) };
      if (axis === "w") {
        const w = (size.w || 1) + delta;
        size.w = Math.max(0.4, w);
      } else if (axis === "h") {
        const h = (size.h || 1) + delta;
        size.h = Math.max(0.4, h);
      } else {
        const w = (size.w || 1) + delta;
        const h = (size.h || 1) + delta;
        size.w = Math.max(0.4, w);
        size.h = Math.max(0.4, h);
      }
      next.size = size;
    } else if (field === "frame_height") {
      const entry = artworkMapRef.current.get(next.artwork_id);
      if (!entry?.mesh || !entry?.baseSize) return;
      const scale = entry.mesh.scale;
      const normalized = {
        w: entry.baseSize.w * (scale.x || 1),
        h: entry.baseSize.h * (scale.y || 1),
        d: entry.baseSize.d * (scale.z || 1)
      };
      entry.baseSize = normalized;
      entry.mesh.userData.baseSize = normalized;
      entry.mesh.scale.set(1, 1, 1);
      const border = typeof next.frame_border === "number" ? next.frame_border : DEFAULT_FRAME_BORDER;
      const imageH = entry.imageMesh?.geometry?.parameters?.height || Math.max(0.2, normalized.h - border * 2);
      const newH = Math.max(0.6, (normalized.h || 1) + delta);
      const matH = Math.max(0.2, newH - border * 2);
      const marginV = Math.max(0, (matH - imageH) / 2);
      next.size = { ...normalized, h: newH };
      next.image_margin_v = marginV;
    } else if (field === "brightness") {
      const current = typeof next.brightness === "number" ? next.brightness : 1;
      next.brightness = Math.max(0, Math.min(2, current + delta));
    } else if (field === "frame_border") {
      const current = typeof next.frame_border === "number" ? next.frame_border : DEFAULT_FRAME_BORDER;
      next.frame_border = Math.max(0.05, Math.min(0.6, current + delta));
    } else if (field === "frame_color") {
      next.frame_color = delta;
    } else if (field === "image_margin_h") {
      const current = typeof next.image_margin_h === "number" ? next.image_margin_h : DEFAULT_IMAGE_MARGIN_H;
      next.image_margin_h = Math.max(0, Math.min(0.8, current + delta));
    } else if (field === "image_margin_v") {
      const current = typeof next.image_margin_v === "number" ? next.image_margin_v : DEFAULT_IMAGE_MARGIN_V;
      next.image_margin_v = Math.max(0, Math.min(0.8, current + delta));
    }
    setMoveDraft(next);
    applyMoveDraftToMesh(next);
  };

  const confirmMoveArtwork = async () => {
    if (!moveDraft || !moveDraft.artwork_id) return;
    try {
      const res = await fetch("/api/fast/artworks/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artwork_id: moveDraft.artwork_id,
          position: moveDraft.position,
          rotation: moveDraft.rotation,
          wall_side: moveDraft.wall_side || "",
          size: moveDraft.size,
          brightness: moveDraft.brightness,
          frame_border: moveDraft.frame_border,
          frame_color: moveDraft.frame_color,
          image_margin_h: moveDraft.image_margin_h,
          image_margin_v: moveDraft.image_margin_v
        })
      });
      if (res.ok) {
        setMovingArtworkId("");
        setMoveDraft(null);
        refreshArtworks(selectedLayoutId);
      }
    } catch {
      // ignore
    }
  };

  const refreshLayouts = async () => {
    try {
      const res = await fetch(`/api/fast/layouts?room_id=${DEFAULT_ROOM_ID}`);
      const data = await res.json();
      if (res.ok && Array.isArray(data.items)) {
        const items = data.items.filter((layout) => layout.status !== "deleted");
        setLayouts(items);
        const current = items.find((layout) => layout.layout_id === selectedLayoutId);
        if (!current && items.length > 0) {
          setSelectedLayoutId(items[0].layout_id);
          setLayoutEdit({
            title: items[0].title || "",
            description: items[0].description || ""
          });
        }
      }
    } catch {
      setLayouts([]);
    }
  };

  const refreshArtworks = async (layoutId) => {
    if (!layoutId) {
      setArtworks([]);
      return;
    }
    setLoadingArtworks(true);
    try {
      const res = await fetch(`/api/fast/artworks?layout_id=${layoutId}`);
      const data = await res.json();
      if (res.ok && Array.isArray(data.items)) {
        setArtworks(data.items);
      } else {
        setArtworks([]);
      }
    } catch {
      setArtworks([]);
    } finally {
      setLoadingArtworks(false);
    }
  };

  useEffect(() => {
    refreshLayouts();
  }, []);

  useEffect(() => {
    if (selectedLayoutId) {
      refreshArtworks(selectedLayoutId);
      syncQuery(selectedLayoutId, viewMode);
    }
  }, [selectedLayoutId]);

  useEffect(() => {
    syncQuery(selectedLayoutId, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!selectedLayout) return;
    setLayoutEdit({
      title: selectedLayout.title || "",
      description: selectedLayout.description || ""
    });
  }, [selectedLayoutId, layouts]);

  useEffect(() => {
    if (viewMode === "scene") {
      setHasMoved(false);
      setActiveArtwork(null);
      activeArtworkRef.current = null;
    }
  }, [viewMode]);

  useEffect(() => {
    if (!moveDraft) return;
    if (transformDraggingRef.current) return;
    applyMoveDraftToMesh(moveDraft);
  }, [moveDraft]);

  useEffect(() => {
    const controls = transformControlsRef.current;
    if (!controls) return;
    if (!moveDraft || !movingArtworkId) {
      controls.detach();
      controls.enabled = false;
      controls.visible = false;
      return;
    }
    const entry = artworkMapRef.current.get(movingArtworkId);
    if (!entry?.mesh) return;
    if (!moveDraft.freeMove && transformMode === "scale") {
      setTransformMode("translate");
      return;
    }
    controls.attach(entry.mesh);
    controls.enabled = true;
    controls.visible = true;
    controls.setMode(transformMode);
    if (moveDraft.freeMove) {
      controls.showX = true;
      controls.showY = true;
      controls.showZ = true;
    } else if (transformMode === "translate") {
      controls.showX = false;
      controls.showY = true;
      controls.showZ = true;
    } else if (transformMode === "rotate") {
      controls.showX = true;
      controls.showY = false;
      controls.showZ = false;
    } else {
      controls.showX = false;
      controls.showY = false;
      controls.showZ = false;
    }
  }, [moveDraft, movingArtworkId, transformMode]);

  useEffect(() => {
    if (!isSceneMode) {
      setLoading(false);
      return () => { };
    }
    const mount = mountRef.current;
    if (!mount || !selectedLayoutId) {
      setLoading(false);
      return () => { };
    }
    setLoading(true);

    let disposed = false;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xd6d6d6);
    scene.fog = new THREE.Fog(0xd6d6d6, 20, 90);

    const camera = new THREE.PerspectiveCamera(
      50,
      mount.clientWidth / mount.clientHeight,
      0.1,
      200
    );
    camera.position.set(0, 1.7, 40);
    camera.rotation.order = "YXZ";
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    const transformControls = new TransformControls(camera, renderer.domElement);
    transformControls.enabled = false;
    transformControls.visible = false;
    scene.add(transformControls);
    transformControlsRef.current = transformControls;

    const input = {
      dragging: false,
      lastX: 0,
      lastY: 0,
      yaw: 0,
      pitch: 0,
      keys: new Set()
    };

    const ambientLight = new THREE.HemisphereLight(0xffffff, 0xffeedd, 0.5);
    scene.add(ambientLight);

    for (let z = -50; z <= 50; z += 20) {
      const skylight = new THREE.DirectionalLight(0xffffff, 0.8);
      skylight.position.set(5, 20, z);
      skylight.castShadow = true;
      skylight.shadow.camera.near = 0.1;
      skylight.shadow.camera.far = 40;
      skylight.shadow.camera.right = 15;
      skylight.shadow.camera.left = -15;
      skylight.shadow.camera.top = 15;
      skylight.shadow.camera.bottom = -15;
      skylight.shadow.bias = -0.001;
      scene.add(skylight);

    }

    const floorTexture = createParquetTexture();
    const floorNoise = createNoiseTexture(256, 22, 140);
    if (floorNoise) floorNoise.repeat.set(6, 30);
    const floorMaterial = new THREE.MeshStandardMaterial({
      map: floorTexture || undefined,
      roughness: 0.55,
      metalness: 0.05,
      side: THREE.DoubleSide,
      bumpMap: floorNoise || undefined,
      roughnessMap: floorNoise || undefined,
      bumpScale: 0.03
    });

    const wallTexture = createWallTexture();
    const wallNoise = createNoiseTexture(256, 18, 150);
    if (wallNoise) wallNoise.repeat.set(2, 3);
    const wallMaterial = new THREE.MeshStandardMaterial({
      map: wallTexture || undefined,
      roughness: 0.9,
      metalness: 0,
      bumpMap: wallNoise || undefined,
      roughnessMap: wallNoise || undefined,
      bumpScale: 0.015
    });

    const ceilingMaterial = new THREE.MeshStandardMaterial({
      color: 0xf5f5f5,
      roughness: 1,
      side: THREE.BackSide
    });

    const goldMaterial = new THREE.MeshStandardMaterial({
      color: 0xb48840,
      metalness: 0.7,
      roughness: 0.35
    });

    const marbleTexture = createMarbleTexture();
    const marbleNoise = createNoiseTexture(256, 15, 160);
    if (marbleNoise) marbleNoise.repeat.set(2, 6);
    const marbleMaterial = new THREE.MeshStandardMaterial({
      map: marbleTexture || undefined,
      color: 0xffffff,
      roughness: 0.45,
      metalness: 0.05,
      roughnessMap: marbleNoise || undefined,
      bumpMap: marbleNoise || undefined,
      bumpScale: 0.02
    });

    const galleryWidth = GALLERY_WIDTH;
    const galleryLength = GALLERY_LENGTH;
    const wallHeight = WALL_HEIGHT;
    const galleryGroup = new THREE.Group();
    galleryGroupRef.current = galleryGroup;
    const bounds = {
      minX: -galleryWidth / 2 + 0.8,
      maxX: galleryWidth / 2 - 0.8,
      minZ: -galleryLength / 2 + 0.8,
      maxZ: galleryLength / 2 - 0.8
    };

    const gltfLoader = new GLTFLoader();
    gltfLoaderRef.current = gltfLoader;
    const goldTextureLoader = new THREE.TextureLoader();
    const configureGoldTexture = (texture, isColor = false) => {
      if (!texture) return;
      texture.flipY = false;
      if (isColor) {
        if ("colorSpace" in texture) {
          texture.colorSpace = THREE.SRGBColorSpace;
        } else if ("encoding" in texture) {
          texture.encoding = THREE.sRGBEncoding;
        }
      }
      texture.needsUpdate = true;
    };
    const sofaPositions = [];
    for (let z = -40; z <= 40; z += 30) {
      sofaPositions.push(z);
    }
    gltfLoader.load(
      SOFA_MODEL_URL,
      (gltf) => {
        if (disposed) return;
        const baseModel = gltf.scene;
        const applyShadows = (obj) => {
          obj.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });
        };
        applyShadows(baseModel);
        const box = new THREE.Box3().setFromObject(baseModel);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        baseModel.position.sub(center);
        const maxSpan = Math.max(size.x, size.z);
        if (maxSpan > 0) {
          const scale = 3.5 / maxSpan;
          baseModel.scale.set(scale, scale, scale);
        }
        const adjustedBox = new THREE.Box3().setFromObject(baseModel);
        const adjustedSize = new THREE.Vector3();
        adjustedBox.getSize(adjustedSize);
        const groundOffsetY = adjustedSize.y / 2;
        baseModel.userData.groundOffsetY = groundOffsetY;
        sofaPositions.forEach((z) => {
          const clone = baseModel.clone(true);
          applyShadows(clone);
          clone.position.set(0, baseModel.userData.groundOffsetY || 0, z);
          galleryGroup.add(clone);
        });
      },
      undefined,
      () => { }
    );

    gltfLoader.load(
      SPORTS_WOMAN_URL,
      (gltf) => {
        if (disposed) return;
        const model = gltf.scene;
        const goldMaterial = new THREE.MeshStandardMaterial({
          color: 0xffffff,
          metalness: 1,
          roughness: 0.4
        });
        goldTextureLoader.load(GOLD_BASECOLOR_URL, (texture) => {
          configureGoldTexture(texture, true);
          goldMaterial.map = texture;
          goldMaterial.needsUpdate = true;
        });
        goldTextureLoader.load(GOLD_ROUGHNESS_URL, (texture) => {
          configureGoldTexture(texture);
          goldMaterial.roughnessMap = texture;
          goldMaterial.needsUpdate = true;
        });
        goldTextureLoader.load(GOLD_METALLIC_URL, (texture) => {
          configureGoldTexture(texture);
          goldMaterial.metalnessMap = texture;
          goldMaterial.needsUpdate = true;
        });
        goldTextureLoader.load(GOLD_NORMAL_URL, (texture) => {
          configureGoldTexture(texture);
          goldMaterial.normalMap = texture;
          goldMaterial.needsUpdate = true;
        });
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            child.material = goldMaterial;
          }
        });
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        model.position.sub(center);
        if (size.y > 0) {
          const targetHeight = 2.1;
          const scale = targetHeight / size.y;
          model.scale.set(scale, scale, scale);
        }
        const adjustedBox = new THREE.Box3().setFromObject(model);
        const adjustedSize = new THREE.Vector3();
        adjustedBox.getSize(adjustedSize);
        model.position.y += adjustedSize.y / 2;
        model.position.z += 0;
        galleryGroup.add(model);
      },
      undefined,
      () => { }
    );

    const floorGeo = new THREE.PlaneGeometry(galleryWidth, galleryLength);
    const floor = new THREE.Mesh(floorGeo, floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    galleryGroup.add(floor);

    const wallGeo = new THREE.PlaneGeometry(galleryLength, wallHeight);
    const leftWall = new THREE.Mesh(wallGeo, wallMaterial);
    leftWall.rotation.y = Math.PI / 2;
    leftWall.position.set(-galleryWidth / 2, wallHeight / 2, 0);
    leftWall.receiveShadow = true;
    galleryGroup.add(leftWall);

    const rightWall = new THREE.Mesh(wallGeo, wallMaterial);
    rightWall.rotation.y = -Math.PI / 2;
    rightWall.position.set(galleryWidth / 2, wallHeight / 2, 0);
    rightWall.receiveShadow = true;
    galleryGroup.add(rightWall);

    const baseGeo = new THREE.BoxGeometry(galleryLength, 0.6, 0.3);
    const leftBase = new THREE.Mesh(baseGeo, marbleMaterial);
    leftBase.position.set(-galleryWidth / 2 + 0.15, 0.3, 0);
    leftBase.rotation.y = Math.PI / 2;
    galleryGroup.add(leftBase);
    const rightBase = leftBase.clone();
    rightBase.position.set(galleryWidth / 2 - 0.15, 0.3, 0);
    galleryGroup.add(rightBase);

    const roofY = galleryWidth / 2 - 0.9;
    const vaultRadius = galleryWidth / 2;
    const gapAngle = Math.PI * 0.35;
    const sideAngle = (Math.PI - gapAngle) / 2;
    const vaultLeftGeo = new THREE.CylinderGeometry(
      vaultRadius,
      vaultRadius,
      galleryLength,
      64,
      1,
      true,
      0,
      sideAngle
    );
    const vaultRightGeo = new THREE.CylinderGeometry(
      vaultRadius,
      vaultRadius,
      galleryLength,
      64,
      1,
      true,
      sideAngle + gapAngle,
      sideAngle
    );
    const vaultLeft = new THREE.Mesh(vaultLeftGeo, ceilingMaterial);
    const vaultRight = new THREE.Mesh(vaultRightGeo, ceilingMaterial);
    vaultLeft.rotation.z = Math.PI / 2;
    vaultLeft.rotation.x = 0;
    vaultLeft.rotation.y = Math.PI / 2;
    vaultLeft.position.set(0, roofY, 0);
    vaultRight.rotation.z = Math.PI / 2;
    vaultRight.rotation.x = 0;
    vaultRight.rotation.y = Math.PI / 2;
    vaultRight.position.set(0, roofY, 0);
    galleryGroup.add(vaultLeft);
    galleryGroup.add(vaultRight);

    const skylightTexture = createSkylightTexture();
    const skylightGeo = new THREE.CylinderGeometry(
      vaultRadius - 0.8,
      vaultRadius - 0.8,
      galleryLength - 8,
      32,
      1,
      true,
      sideAngle,
      gapAngle
    );
    const skylightMat = new THREE.MeshStandardMaterial({
      map: skylightTexture || undefined,
      color: 0xffffff,
      roughness: 0.2,
      metalness: 0.0,
      transparent: true,
      opacity: 0.6,
      side: THREE.BackSide
    });
    const skylight = new THREE.Mesh(skylightGeo, skylightMat);
    skylight.rotation.z = Math.PI / 2;
    skylight.rotation.x = 0;
    skylight.rotation.y = Math.PI / 2;
    skylight.position.set(0, roofY, 0);
    galleryGroup.add(skylight);

    for (let z = -galleryLength / 2 + 10; z < galleryLength / 2; z += 20) {
      const pillarGeo = new THREE.CylinderGeometry(0.45, 0.5, wallHeight, 16);
      const pLeft = new THREE.Mesh(pillarGeo, marbleMaterial);
      pLeft.position.set(-galleryWidth / 2 + 0.6, wallHeight / 2, z);
      galleryGroup.add(pLeft);

      const pRight = new THREE.Mesh(pillarGeo, marbleMaterial);
      pRight.position.set(galleryWidth / 2 - 0.6, wallHeight / 2, z);
      galleryGroup.add(pRight);

      const capitalGeo = new THREE.CylinderGeometry(0.65, 0.55, 0.3, 16);
      const capLeft = new THREE.Mesh(capitalGeo, goldMaterial);
      capLeft.position.set(pLeft.position.x, wallHeight - 0.15, z);
      galleryGroup.add(capLeft);
      const capRight = capLeft.clone();
      capRight.position.set(pRight.position.x, wallHeight - 0.15, z);
      galleryGroup.add(capRight);

      const archRibGeo = new THREE.TorusGeometry(galleryWidth / 2 - 0.2, 0.35, 8, 32, Math.PI);
      const archRib = new THREE.Mesh(archRibGeo, wallMaterial);
      archRib.position.set(0, wallHeight, z);
      galleryGroup.add(archRib);
    }

    const textureLoader = new THREE.TextureLoader();
    textureLoaderRef.current = textureLoader;
    const videoPlaceholderTexture = createVideoPlaceholderTexture();
    function applyArtworkTexture(material, imageUrl, imageMesh, entry, placeholderTexture) {
      if (!imageUrl) {
        if (placeholderTexture) {
          material.map = placeholderTexture;
          material.needsUpdate = true;
          if (entry && !entry.placeholderMap) {
            entry.placeholderMap = placeholderTexture;
          }
        }
        return;
      }
      textureLoader.load(
        imageUrl,
        (texture) => {
          if (disposed) return;
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          material.map = texture;
          material.needsUpdate = true;
          if (entry && !entry.placeholderMap) {
            entry.placeholderMap = texture;
          }
          if (imageMesh && entry) {
            applyImageAspect(entry);
          }
        },
        undefined,
        () => { }
      );
    }

    function createPainting(
      width,
      height,
      imageUrl,
      label,
      brightness,
      frameBorder,
      frameColor,
      marginH,
      marginV,
      placeholderTexture,
      isVideo
    ) {
      const group = new THREE.Group();
      const frameMat = new THREE.MeshStandardMaterial({
        color: frameColor || "#b48840",
        metalness: 0.7,
        roughness: 0.35
      });

      const border = typeof frameBorder === "number" ? frameBorder : DEFAULT_FRAME_BORDER;
      const matW = width - border * 2;
      const matH = height - border * 2;
      const depth = 0.1;
      const topBottomGeo = new THREE.BoxGeometry(width, border, depth);
      const sideGeo = new THREE.BoxGeometry(border, Math.max(0.2, height - border * 2), depth);
      const topFrame = new THREE.Mesh(topBottomGeo, frameMat);
      const bottomFrame = new THREE.Mesh(topBottomGeo, frameMat);
      const leftFrame = new THREE.Mesh(sideGeo, frameMat);
      const rightFrame = new THREE.Mesh(sideGeo, frameMat);
      topFrame.position.set(0, (height - border) / 2, 0);
      bottomFrame.position.set(0, -(height - border) / 2, 0);
      leftFrame.position.set(-(width - border) / 2, 0, 0);
      rightFrame.position.set((width - border) / 2, 0, 0);
      topFrame.castShadow = true;
      bottomFrame.castShadow = true;
      leftFrame.castShadow = true;
      rightFrame.castShadow = true;
      group.add(topFrame);
      group.add(bottomFrame);
      group.add(leftFrame);
      group.add(rightFrame);
      const matGeo = new THREE.PlaneGeometry(matW, matH);
      const hasImage = Boolean(imageUrl);
      const matMat = new THREE.MeshStandardMaterial({
        color: isVideo ? new THREE.Color(0.02, 0.02, 0.02) : hasImage
          ? new THREE.Color(0.93, 0.92, 0.88)
          : new THREE.Color(Math.random() * 0.5, Math.random() * 0.4, Math.random() * 0.3),
        roughness: hasImage ? 0.5 : 0.6
      });
      const matMesh = new THREE.Mesh(matGeo, matMat);
      matMesh.position.z = 0.055;
      group.add(matMesh);
      const safeMarginH = typeof marginH === "number" ? marginH : DEFAULT_IMAGE_MARGIN_H;
      const safeMarginV = typeof marginV === "number" ? marginV : DEFAULT_IMAGE_MARGIN_V;
      const imageGeo = new THREE.PlaneGeometry(
        Math.max(0.2, matW - safeMarginH * 2),
        Math.max(0.2, matH - safeMarginV * 2)
      );
      const imageMat = new THREE.MeshStandardMaterial({
        color: hasImage ? new THREE.Color(1, 1, 1) : new THREE.Color(0.2, 0.2, 0.2),
        roughness: 0.4
      });
      const imageMesh = new THREE.Mesh(imageGeo, imageMat);
      imageMesh.position.z = 0.07;
      group.add(imageMesh);
      group.userData.baseSize = { w: width, h: height, d: 0.1 };
      group.userData.baseBorder = border;
      group.userData.canvasMat = imageMat;
      group.userData.canvasMesh = imageMesh;
      group.userData.matMesh = matMesh;
      group.userData.frameMaterial = frameMat;
      group.userData.frameParts = [topFrame, bottomFrame, leftFrame, rightFrame];

      const rectLight = new THREE.RectAreaLight(0xffffff, 1, matW, matH);
      rectLight.position.set(0, 0, 1.4);
      rectLight.lookAt(0, 0, 0);
      group.add(rectLight);
      group.userData.rectLight = rectLight;
      applyArtworkLight(rectLight, brightness);

      const labelTexture = createLabelTexture(label);
      if (labelTexture) {
        const labelGeo = new THREE.PlaneGeometry(width * 0.8, 0.28);
        const labelMat = new THREE.MeshBasicMaterial({
          map: labelTexture,
          transparent: true
        });
        const labelMesh = new THREE.Mesh(labelGeo, labelMat);
        labelMesh.position.set(0, -height / 2 - 0.2, 0.07);
        group.add(labelMesh);
      }
      const entry = {
        mesh: group,
        material: imageMat,
        imageMesh,
        matMesh,
        light: rectLight,
        baseSize: group.userData.baseSize,
        baseBorder: border,
        frameMaterial: frameMat,
        frameParts: group.userData.frameParts
      };
      applyArtworkTexture(imageMat, imageUrl, imageMesh, entry, placeholderTexture);
      return entry;
    }

    const isObjectArtwork = (artwork, binding) => {
      const kind = (artwork?.kind || "").toLowerCase();
      if (["object", "sculpture", "model", "3d"].includes(kind)) return true;
      return isModelUrl(binding?.asset_url);
    };

    async function loadArtworks() {
      let items = [];
      try {
        const res = await fetch(`/api/fast/artworks?layout_id=${selectedLayoutId}`);
        const data = await res.json();
        if (res.ok && Array.isArray(data.items) && data.items.length > 0) {
          items = data.items;
        }
      } catch {
        // ignore fetch errors
      }
      if (!items.length) {
        const fallback = buildFallbackArtworks();
        return fallback.map((artwork) => ({ artwork, binding: {} }));
      }
      return items;
    }

    loadArtworks().then((items) => {
      if (disposed) return;
      items.forEach(({ artwork, binding }) => {
        if (!artwork) return;
        const size = artwork.size || {};
        const pos = artwork.position || {};
        const rot = artwork.rotation || {};
        const width = size.w || 2;
        const height = size.h || 2;
        const assetUrl = binding?.asset_url || "";
        if (isObjectArtwork(artwork, binding)) {
          if (!assetUrl) return;
          gltfLoader.load(
            assetUrl,
            (gltf) => {
              if (disposed) return;
              const model = gltf.scene;
              model.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                }
              });
              const box = new THREE.Box3().setFromObject(model);
              const sizeVec = new THREE.Vector3();
              const center = new THREE.Vector3();
              box.getSize(sizeVec);
              box.getCenter(center);
              model.position.sub(center);
              if (size.w && size.h && sizeVec.y) {
                const scale = size.h / sizeVec.y;
                model.scale.set(scale, scale, scale);
              }
              const adjustedBox = new THREE.Box3().setFromObject(model);
              const adjustedSize = new THREE.Vector3();
              adjustedBox.getSize(adjustedSize);
              model.position.set(pos.x || 0, (pos.y || 0) + adjustedSize.y / 2, pos.z || 0);
              model.rotation.set(rot.x || 0, rot.y || 0, rot.z || 0);
              model.userData.baseSize = {
                w: adjustedSize.x || 1,
                h: adjustedSize.y || 1,
                d: adjustedSize.z || 1
              };
              galleryGroup.add(model);
              const entry = {
                mesh: model,
                artwork,
                binding: binding || {}
              };
              artworkMapRef.current.set(artwork.artwork_id, entry);
            },
            undefined,
            () => { }
          );
          return;
        }
        const isVideo = isVideoUrl(assetUrl);
        const previewUrl = isVideo ? (binding?.asset_thumbnail_url || "") : assetUrl;
        const placeholderTexture = isVideo && !previewUrl ? videoPlaceholderTexture : null;
        const paintingEntry = createPainting(
          width,
          height,
          previewUrl,
          artwork.label || "",
          typeof artwork.brightness === "number" ? artwork.brightness : 1,
          typeof artwork.frame_border === "number" ? artwork.frame_border : DEFAULT_FRAME_BORDER,
          artwork.frame_color || "#b48840",
          typeof artwork.image_margin_h === "number" ? artwork.image_margin_h : DEFAULT_IMAGE_MARGIN_H,
          typeof artwork.image_margin_v === "number" ? artwork.image_margin_v : DEFAULT_IMAGE_MARGIN_V,
          placeholderTexture,
          isVideo
        );
        paintingEntry.mesh.position.set(pos.x || 0, pos.y || 0, pos.z || 0);
        paintingEntry.mesh.rotation.set(rot.x || 0, rot.y || 0, rot.z || 0);
        galleryGroup.add(paintingEntry.mesh);
        if (assetUrl) {
          paintingEntry.light.position.set(0, 0, 1.4);
        }
        if (isVideo && assetUrl) {
          const video = document.createElement("video");
          video.src = assetUrl;
          video.crossOrigin = "anonymous";
          video.muted = !audioEnabledRef.current;
          video.loop = true;
          video.playsInline = true;
          video.preload = "metadata";
          video.setAttribute("playsinline", "true");
          const videoTexture = new THREE.VideoTexture(video);
          videoTexture.minFilter = THREE.LinearFilter;
          videoTexture.magFilter = THREE.LinearFilter;
          videoTexture.generateMipmaps = false;
          paintingEntry.video = { element: video, texture: videoTexture, playing: false };
          video.addEventListener("loadedmetadata", () => {
            applyImageAspect(paintingEntry);
          });
        }
        paintingEntry.artwork = artwork;
        paintingEntry.binding = binding || {};
        artworkMapRef.current.set(artwork.artwork_id, paintingEntry);
      });
      setLoading(false);
    });

    scene.add(galleryGroup);

    const onPointerDown = (event) => {
      if (transformControlsRef.current?.dragging) return;
      if (event.isPrimary === false) return;
      input.dragging = true;
      input.lastX = event.clientX;
      input.lastY = event.clientY;
    };

    const onPointerMove = (event) => {
      if (transformControlsRef.current?.dragging) return;
      if (!input.dragging) return;
      const dx = event.clientX - input.lastX;
      const dy = event.clientY - input.lastY;
      input.lastX = event.clientX;
      input.lastY = event.clientY;
      const sensitivity = 0.0025;
      input.yaw -= dx * sensitivity;
      input.pitch -= dy * sensitivity;
      const limit = Math.PI / 2.4;
      input.pitch = Math.max(-limit, Math.min(limit, input.pitch));
    };

    const onPointerUp = () => {
      input.dragging = false;
    };

    const onTransformDrag = (event) => {
      transformDraggingRef.current = event.value;
    };

    const onTransformChange = () => {
      const draft = moveDraftRef.current;
      if (!draft?.artwork_id) return;
      const entry = artworkMapRef.current.get(draft.artwork_id);
      if (!entry?.mesh) return;
      const pos = entry.mesh.position;
      const rot = entry.mesh.rotation;
      const next = {
        ...draft,
        position: { x: pos.x, y: pos.y, z: pos.z },
        rotation: { x: rot.x, y: rot.y, z: rot.z }
      };
      if (!draft.freeMove) {
        const wallSide = next.wall_side || (pos.x < 0 ? "left" : "right");
        const wallX = wallSide === "left" ? -GALLERY_WIDTH / 2 + WALL_OFFSET : GALLERY_WIDTH / 2 - WALL_OFFSET;
        next.wall_side = wallSide;
        next.position.x = wallX;
        next.rotation = {
          x: rot.x,
          y: wallSide === "left" ? Math.PI / 2 : -Math.PI / 2,
          z: 0
        };
        entry.mesh.position.x = wallX;
        entry.mesh.rotation.y = next.rotation.y;
        entry.mesh.rotation.z = 0;
      } else if (transformModeRef.current === "scale") {
        const baseSize = entry.mesh.userData.baseSize || draft.size || { w: 1, h: 1, d: 1 };
        const scale = entry.mesh.scale;
        next.size = {
          w: baseSize.w * scale.x,
          h: baseSize.h * scale.y,
          d: baseSize.d * scale.z
        };
      }
      setMoveDraft(next);
    };

    const normalizeKey = (key) => {
      if (key === "ArrowUp") return "arrowup";
      if (key === "ArrowDown") return "arrowdown";
      if (key === "ArrowLeft") return "arrowleft";
      if (key === "ArrowRight") return "arrowright";
      return key.toLowerCase();
    };

    const onKeyDown = (event) => {
      input.keys.add(normalizeKey(event.key));
    };

    const onKeyUp = (event) => {
      input.keys.delete(normalizeKey(event.key));
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    transformControls.addEventListener("dragging-changed", onTransformDrag);
    transformControls.addEventListener("objectChange", onTransformChange);

    let frameId = 0;
    const tempPosition = new THREE.Vector3();
    const animate = () => {
      frameId = window.requestAnimationFrame(animate);
      const move = moveRef.current;
      const look = lookRef.current;
      input.yaw -= look.x * 0.03;
      input.pitch -= look.y * 0.03;
      const limit = Math.PI / 2.4;
      input.pitch = Math.max(-limit, Math.min(limit, input.pitch));

      camera.rotation.y = input.yaw;
      camera.rotation.x = input.pitch;
      camera.position.y = 1.7;

      const forward = new THREE.Vector3(0, 0, -1).applyAxisAngle(new THREE.Vector3(0, 1, 0), input.yaw);
      const right = new THREE.Vector3(1, 0, 0).applyAxisAngle(new THREE.Vector3(0, 1, 0), input.yaw);
      const speed = 0.08;
      const keyForward =
        (input.keys.has("w") ? 1 : 0) -
        (input.keys.has("s") ? 1 : 0) +
        (input.keys.has("arrowup") ? 1 : 0) -
        (input.keys.has("arrowdown") ? 1 : 0);
      const keyTurn =
        (input.keys.has("arrowright") ? 1 : 0) -
        (input.keys.has("arrowleft") ? 1 : 0);
      const keyRight =
        (input.keys.has("d") ? 1 : 0) - (input.keys.has("a") ? 1 : 0);
      const moveX = (keyRight + move.x) * speed;
      const moveZ = (keyForward + move.y) * speed;
      input.yaw -= keyTurn * 0.03;
      if (!movedRef.current) {
        const moved =
          Math.abs(moveX) > 0.0001 ||
          Math.abs(moveZ) > 0.0001 ||
          Math.abs(keyTurn) > 0.0001 ||
          Math.abs(look.x) > 0.0001 ||
          Math.abs(look.y) > 0.0001;
        if (moved) {
          movedRef.current = true;
          setHasMoved(true);
        }
      }
      camera.position.addScaledVector(right, moveX);
      camera.position.addScaledVector(forward, moveZ);
      camera.position.x = Math.max(bounds.minX, Math.min(bounds.maxX, camera.position.x));
      camera.position.z = Math.max(bounds.minZ, Math.min(bounds.maxZ, camera.position.z));
      let nearest = null;
      let nearestDist = Infinity;
      const startVideoDist = 2;
      const stopVideoDist = 2.5;
      artworkMapRef.current.forEach((entry) => {
        entry.mesh.getWorldPosition(tempPosition);
        const dist = tempPosition.distanceTo(camera.position);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = entry;
        }
        if (entry.video && entry.material) {
          if (dist <= startVideoDist) {
            if (!entry.video.playing) {
              if (!entry.placeholderMap && entry.material.map && entry.material.map !== entry.video.texture) {
                entry.placeholderMap = entry.material.map;
              }
              entry.material.map = entry.video.texture;
              entry.material.needsUpdate = true;
              entry.video.element.muted = !audioEnabledRef.current;
              entry.video.element.play().catch(() => { });
              entry.video.playing = true;
            }
          } else if (dist >= stopVideoDist) {
            if (entry.video.playing) {
              entry.video.element.pause();
              if (entry.placeholderMap) {
                entry.material.map = entry.placeholderMap;
                entry.material.needsUpdate = true;
              }
              entry.video.playing = false;
            }
          }
        }
      });
      const threshold = 3;
      if (nearest && nearestDist <= threshold) {
        if (!activeArtworkRef.current || activeArtworkRef.current.artwork_id !== nearest.artwork.artwork_id) {
          activeArtworkRef.current = { ...nearest.artwork, binding: nearest.binding || {} };
          setActiveArtwork(activeArtworkRef.current);
        }
      } else if (activeArtworkRef.current) {
        activeArtworkRef.current = null;
        setActiveArtwork(null);
      }
      renderer.render(scene, camera);
    };

    const handleResize = () => {
      if (!mount) return;
      const width = mount.clientWidth || window.innerWidth;
      const height = mount.clientHeight || window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);
    animate();

    return () => {
      disposed = true;
      if (cameraRef.current === camera) {
        cameraRef.current = null;
      }
      artworkMapRef.current.forEach((entry) => {
        if (entry.video?.element) {
          entry.video.element.pause();
          entry.video.element.src = "";
          entry.video.element.load();
        }
      });
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      transformControls.removeEventListener("dragging-changed", onTransformDrag);
      transformControls.removeEventListener("objectChange", onTransformChange);
      transformControls.detach();
      transformControlsRef.current = null;
      window.cancelAnimationFrame(frameId);
      renderer.dispose();
      if (renderer.domElement && mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      if (floorTexture) floorTexture.dispose();
      if (wallTexture) wallTexture.dispose();
      floorGeo.dispose();
      wallGeo.dispose();
      vaultGeo.dispose();
      artworkMapRef.current.clear();
    };
  }, [isSceneMode, selectedLayoutId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const done = params.get("media_picker_done");
    const rawTarget = params.get("target") || "";
    let decodedTarget = rawTarget;
    for (let i = 0; i < 2; i += 1) {
      try {
        decodedTarget = decodeURIComponent(decodedTarget);
      } catch {
        break;
      }
    }
    if (done !== "1" || !decodedTarget.startsWith("artwork:")) return;
    const artworkId = decodedTarget.replace("artwork:", "");
    let payload = null;
    try {
      payload = JSON.parse(window.sessionStorage.getItem("contentos_media_picker_payload") || "null");
    } catch {
      payload = null;
    }
    const selection = Array.isArray(payload?.versions) ? payload.versions[0] : null;
    let assetId = selection?.assetId || "";
    const mediaItemId = selection?.mediaItemId || "";
    const mediaVersionId = selection?.mediaVersionId || "";
    if (assetId.startsWith("mi_")) {
      assetId = "";
    }
    if (!assetId && !mediaItemId) return;
    const immediateUrl = selection?.url || selection?.thumbnailUrl || "";
    const isModelSelection = isModelUrl(immediateUrl);
    fetch("/api/fast/artworks/update", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        artwork_id: artworkId,
        asset_id: assetId,
        media_item_id: mediaItemId,
        media_version_id: mediaVersionId,
        ...(isModelSelection ? { kind: "object" } : {})
      })
    })
      .then((res) => res.json())
      .then(() => {
        const entry = artworkMapRef.current.get(artworkId);
        const binding = {
          asset_id: assetId,
          media_item_id: mediaItemId,
          media_version_id: mediaVersionId,
          asset_url: immediateUrl,
          asset_thumbnail_url: selection?.thumbnailUrl || ""
        };
        if (entry) {
          entry.binding = { ...entry.binding, ...binding };
          if (isModelSelection && gltfLoaderRef.current && galleryGroupRef.current) {
            if (entry.video?.element) {
              entry.video.element.pause();
              entry.video.element.src = "";
              entry.video.element.load();
              entry.video = null;
            }
            if (entry.mesh) {
              galleryGroupRef.current.remove(entry.mesh);
            }
            const artworkData = { ...(entry.artwork || {}), kind: "object" };
            const pos = artworkData.position || { x: 0, y: 0, z: 0 };
            const rot = artworkData.rotation || { x: 0, y: 0, z: 0 };
            const baseSize = artworkData.size || { w: 2.2, h: 1.6, d: 0.1 };
            gltfLoaderRef.current.load(
              binding.asset_url,
              (gltf) => {
                const model = gltf.scene;
                model.traverse((child) => {
                  if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                  }
                });
                const box = new THREE.Box3().setFromObject(model);
                const sizeVec = new THREE.Vector3();
                const center = new THREE.Vector3();
                box.getSize(sizeVec);
                box.getCenter(center);
                model.position.sub(center);
                if (baseSize.h && sizeVec.y) {
                  const scale = baseSize.h / sizeVec.y;
                  model.scale.set(scale, scale, scale);
                }
                const adjustedBox = new THREE.Box3().setFromObject(model);
                const adjustedSize = new THREE.Vector3();
                adjustedBox.getSize(adjustedSize);
                model.position.set(pos.x || 0, (pos.y || 0) + adjustedSize.y / 2, pos.z || 0);
                model.rotation.set(rot.x || 0, rot.y || 0, rot.z || 0);
                model.userData.baseSize = {
                  w: adjustedSize.x || 1,
                  h: adjustedSize.y || 1,
                  d: adjustedSize.z || 1
                };
                const nextEntry = {
                  mesh: model,
                  artwork: artworkData,
                  binding: { ...binding }
                };
                galleryGroupRef.current.add(model);
                artworkMapRef.current.set(artworkId, nextEntry);
                if (activeArtworkRef.current?.artwork_id === artworkId) {
                  setActiveArtwork({ ...artworkData, binding: { ...binding } });
                }
              },
              undefined,
              () => { }
            );
            return;
          }
          const isVideo = isVideoUrl(binding.asset_url);
          const previewUrl = isVideo ? (binding.asset_thumbnail_url || "") : binding.asset_url;
          const placeholderTexture = isVideo && !previewUrl ? videoPlaceholderRef.current : null;
          if (entry.video?.element && !isVideo) {
            entry.video.element.pause();
            entry.video.element.src = "";
            entry.video.element.load();
            entry.video = null;
          }
          if (entry.material && (previewUrl || placeholderTexture) && textureLoaderRef.current) {
            if (!previewUrl && placeholderTexture) {
              entry.material.map = placeholderTexture;
              entry.material.needsUpdate = true;
              entry.placeholderMap = placeholderTexture;
            } else if (previewUrl) {
              textureLoaderRef.current.load(
                previewUrl,
                (texture) => {
                  texture.wrapS = THREE.ClampToEdgeWrapping;
                  texture.wrapT = THREE.ClampToEdgeWrapping;
                  entry.material.map = texture;
                  entry.material.needsUpdate = true;
                  entry.placeholderMap = texture;
                  const imgW = texture.image?.width || 1;
                  const imgH = texture.image?.height || 1;
                  const aspect = imgW / imgH;
                  const baseSize = entry.artwork?.size || { w: 2.2, h: 1.6, d: 0.1 };
                  const height = baseSize.h || 1.6;
                  const nextSize = { ...baseSize, w: Math.max(0.4, height * aspect) };
                  entry.artwork = { ...(entry.artwork || {}), size: nextSize };
                  applyArtworkSizeToMesh(entry, nextSize);
                  applyImageAspect(entry);
                  fetch("/api/fast/artworks/update", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      artwork_id: artworkId,
                      size: nextSize
                    })
                  }).catch(() => { });
                },
                undefined,
                () => { }
              );
            }
          }
          if (isVideo && binding.asset_url) {
            const video = document.createElement("video");
            video.src = binding.asset_url;
            video.crossOrigin = "anonymous";
            video.muted = !audioEnabledRef.current;
            video.loop = true;
            video.playsInline = true;
            video.preload = "metadata";
            video.setAttribute("playsinline", "true");
            const videoTexture = new THREE.VideoTexture(video);
            videoTexture.minFilter = THREE.LinearFilter;
            videoTexture.magFilter = THREE.LinearFilter;
            videoTexture.generateMipmaps = false;
            entry.video = { element: video, texture: videoTexture, playing: false };
          }
          if (activeArtworkRef.current?.artwork_id === artworkId) {
            setActiveArtwork({ ...activeArtworkRef.current, binding: { ...activeArtworkRef.current.binding, ...binding } });
          }
        }
        setArtworks((prev) =>
          prev.map((item) => {
            if (item.artwork?.artwork_id !== artworkId) return item;
            return {
              ...item,
              artwork: isModelSelection
                ? { ...(item.artwork || {}), kind: "object" }
                : item.artwork,
              binding: { ...item.binding, ...binding }
            };
          })
        );
      })
      .catch(() => { });
    params.delete("media_picker_done");
    params.delete("target");
    const nextQuery = params.toString();
    const base = window.location.pathname;
    window.history.replaceState({}, "", nextQuery ? `${base}?${nextQuery}` : base);
  }, []);

  const openArtworkPicker = (artworkId) => {
    if (!artworkId) return;
    const target = `artwork:${artworkId}`;
    const baseReturn = `/gallerie${selectedLayoutId ? `?layout_id=${selectedLayoutId}` : ""}`;
    const returnPath = isSceneMode ? `${baseReturn}${selectedLayoutId ? "&" : "?"}view=scene` : baseReturn;
    window.location.href = `/media?picker=1&target=${encodeURIComponent(target)}&type=all&multi=0&return=${encodeURIComponent(returnPath)}`;
  };

  const handleCreateLayout = async (event) => {
    event.preventDefault();
    if (!layoutDraft.title.trim()) return;
    try {
      const res = await fetch("/api/fast/layouts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          room_id: DEFAULT_ROOM_ID,
          title: layoutDraft.title.trim(),
          description: layoutDraft.description || ""
        })
      });
      const data = await res.json();
      if (res.ok && data.layout) {
        setLayoutDraft({ title: "", description: "" });
        await refreshLayouts();
        setSelectedLayoutId(data.layout.layout_id);
      }
    } catch {
      // ignore
    }
  };

  const handleUpdateLayout = async () => {
    if (!selectedLayoutId) return;
    try {
      const res = await fetch("/api/fast/layouts/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          layout_id: selectedLayoutId,
          title: layoutEdit.title,
          description: layoutEdit.description
        })
      });
      if (res.ok) {
        refreshLayouts();
      }
    } catch {
      // ignore
    }
  };

  const handleLayoutStatus = async (layoutId, status) => {
    if (!layoutId) return;
    try {
      const res = await fetch("/api/fast/layouts/status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ layout_id: layoutId, status })
      });
      if (res.ok) {
        refreshLayouts();
      }
    } catch {
      // ignore
    }
  };

  const handleAddArtwork = async () => {
    if (!selectedLayoutId) return;
    const draft = {
      label: "Nouveau tableau",
      kind: "painting",
      position: { x: 0, y: 2.4, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      size: { w: 2.2, h: 1.6, d: 0.1 },
      wall_side: "",
      frame_border: DEFAULT_FRAME_BORDER,
      frame_color: "#b48840",
      brightness: 1,
      image_margin_h: DEFAULT_IMAGE_MARGIN_H,
      image_margin_v: DEFAULT_IMAGE_MARGIN_V
    };
    try {
      const res = await fetch("/api/fast/artworks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          layout_id: selectedLayoutId,
          room_id: DEFAULT_ROOM_ID,
          label: draft.label || "Nouveau tableau",
          kind: draft.kind || "painting",
          position: draft.position || { x: 0, y: 2.4, z: 0 },
          rotation: draft.rotation || { x: 0, y: 0, z: 0 },
          size: draft.size || { w: 2.2, h: 1.6, d: 0.1 },
          wall_side: draft.wall_side || "",
          brightness: draft.brightness,
          frame_border: draft.frame_border,
          frame_color: draft.frame_color,
          image_margin_h: draft.image_margin_h,
          image_margin_v: draft.image_margin_v
        })
      });
      if (res.ok) {
        setArtworkDraft(null);
        refreshArtworks(selectedLayoutId);
      } else {
        const data = await res.json().catch(() => ({}));
        console.error("Add artwork failed", res.status, data);
      }
    } catch {
      // ignore
    }
  };

  const handleSaveArtwork = async () => {
    if (!selectedArtworkId || !artworkDraft) return;
    try {
      const res = await fetch("/api/fast/artworks/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artwork_id: selectedArtworkId,
          label: artworkDraft.label,
          kind: artworkDraft.kind,
          position: artworkDraft.position,
          rotation: artworkDraft.rotation,
          size: artworkDraft.size,
          wall_side: artworkDraft.wall_side || "",
          brightness: artworkDraft.brightness,
          frame_border: artworkDraft.frame_border,
          frame_color: artworkDraft.frame_color,
          image_margin_h: artworkDraft.image_margin_h,
          image_margin_v: artworkDraft.image_margin_v
        })
      });
      if (res.ok) {
        refreshArtworks(selectedLayoutId);
      }
    } catch {
      // ignore
    }
  };

  const handleDeleteArtwork = async (artworkId) => {
    if (!artworkId) return;
    if (!window.confirm("Supprimer cet artwork ?")) return;
    try {
      const res = await fetch("/api/fast/artworks/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artwork_id: artworkId })
      });
      if (res.ok) {
        refreshArtworks(selectedLayoutId);
        if (selectedArtworkId === artworkId) {
          setSelectedArtworkId("");
          setArtworkDraft(null);
        }
      }
    } catch {
      // ignore
    }
  };

  const joystickRadius = 48;
  const handleMoveStart = (event) => {
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    target.dataset.active = "1";
    target.dataset.startX = event.clientX;
    target.dataset.startY = event.clientY;
    moveRef.current = { x: 0, y: 0 };
  };

  const handleMove = (event) => {
    const target = event.currentTarget;
    if (target.dataset.active !== "1") return;
    const startX = Number(target.dataset.startX || 0);
    const startY = Number(target.dataset.startY || 0);
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const dist = Math.min(Math.hypot(dx, dy), joystickRadius);
    const angle = Math.atan2(dy, dx);
    const nx = (Math.cos(angle) * dist) / joystickRadius;
    const ny = (Math.sin(angle) * dist) / joystickRadius;
    moveRef.current = { x: nx, y: -ny };
    target.style.setProperty("--stick-x", `${nx * 24}px`);
    target.style.setProperty("--stick-y", `${-ny * 24}px`);
  };

  const handleMoveEnd = (event) => {
    const target = event.currentTarget;
    target.dataset.active = "0";
    moveRef.current = { x: 0, y: 0 };
    target.style.setProperty("--stick-x", "0px");
    target.style.setProperty("--stick-y", "0px");
    target.releasePointerCapture(event.pointerId);
  };

  const handleLookStart = (event) => {
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    target.dataset.active = "1";
    target.dataset.startX = event.clientX;
    target.dataset.startY = event.clientY;
    lookRef.current = { x: 0, y: 0 };
  };

  const handleLook = (event) => {
    const target = event.currentTarget;
    if (target.dataset.active !== "1") return;
    const startX = Number(target.dataset.startX || 0);
    const startY = Number(target.dataset.startY || 0);
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const dist = Math.min(Math.hypot(dx, dy), joystickRadius);
    const angle = Math.atan2(dy, dx);
    const nx = (Math.cos(angle) * dist) / joystickRadius;
    const ny = (Math.sin(angle) * dist) / joystickRadius;
    lookRef.current = { x: nx, y: ny };
    target.style.setProperty("--stick-x", `${nx * 24}px`);
    target.style.setProperty("--stick-y", `${ny * 24}px`);
  };

  const handleLookEnd = (event) => {
    const target = event.currentTarget;
    target.dataset.active = "0";
    lookRef.current = { x: 0, y: 0 };
    target.style.setProperty("--stick-x", "0px");
    target.style.setProperty("--stick-y", "0px");
    target.releasePointerCapture(event.pointerId);
  };

  return (
    <div className="app-shell">
      <Sidebar active="gallerie" />
      <div className="app-main app-main-full gallerie-page">
        {isSceneMode ? (
          selectedLayoutId ? (
            <>
              <div className="gallerie-canvas" ref={mountRef} />
              {loading && <div className="gallerie-loading">Preparation de l&apos;Aile Denon...</div>}
              <div className="gallerie-scene-actions">
                <button
                  type="button"
                  className="video-secondary"
                  onClick={() => {
                    setMovingArtworkId("");
                    setMoveDraft(null);
                    setActiveArtwork(null);
                    setViewMode("manage");
                  }}
                >
                  Retour aux layouts
                </button>
              </div>
              <div className="gallerie-ui">
                <h1 className="page-title-accent">La Grande Galerie</h1>
                {!hasMoved && (
                  <>
                    <span className="gallerie-subtitle">Aile Denon - Peintures Italiennes</span>
                    <p>
                      Promenade virtuelle dans l&apos;une des salles les plus emblematiques du musee, abritant
                      les chefs-d&apos;oeuvre de la Renaissance.
                    </p>
                    <div className="gallerie-controls">
                      <span>
                        <strong>Clic gauche :</strong> Regarder autour
                      </span>
                      <span>
                        <strong>Clic droit :</strong> Se deplacer (Pan)
                      </span>
                      <span>
                        <strong>Molette :</strong> Zoomer / Avancer
                      </span>
                    </div>
                  </>
                )}
                <button
                  type="button"
                  className="gallerie-audio-button"
                  onClick={() => setAudioEnabled((prev) => !prev)}
                >
                  {audioEnabled ? "Son activé" : "Activer le son"}
                </button>
              </div>
              {activeArtwork && (
                <div className="gallerie-overlay">
                  <div className="gallerie-overlay-card">
                    <div className="gallerie-overlay-title">
                      {activeArtwork.label || "Tableau"}
                    </div>
                    {activeArtwork.binding?.asset_thumbnail_url && (
                      <img
                        className="gallerie-overlay-thumb"
                        src={activeArtwork.binding.asset_thumbnail_url}
                        alt="media"
                      />
                    )}
                    <button
                      type="button"
                      className="gallerie-overlay-button video-secondary"
                      onClick={() => openArtworkPicker(activeArtwork.artwork_id)}
                    >
                      Changer le media
                    </button>
                    {!movingArtworkId && (
                      <button
                        type="button"
                        className="gallerie-overlay-button video-secondary"
                        onClick={startMoveArtwork}
                      >
                        Deplacer le tableau
                      </button>
                    )}
                  </div>
                </div>
              )}
              {movingArtworkId && (
                <div className="gallerie-move-panel">
                  <button type="button" className="gallerie-move-primary video-primary" onClick={confirmMoveArtwork}>
                    Placer ici
                  </button>
                  <div className="gallerie-move-controls">
                    <div className="gallerie-move-group">
                      <span>Gizmo</span>
                      <div className="gallerie-move-buttons">
                        <button
                          type="button"
                          className={transformMode === "translate" ? "is-active" : ""}
                          onClick={() => setTransformMode("translate")}
                        >
                          Deplacer
                        </button>
                        <button
                          type="button"
                          className={transformMode === "rotate" ? "is-active" : ""}
                          onClick={() => setTransformMode("rotate")}
                        >
                          Incliner
                        </button>
                        {moveDraft?.freeMove && (
                          <button
                            type="button"
                            className={transformMode === "scale" ? "is-active" : ""}
                            onClick={() => setTransformMode("scale")}
                          >
                            Echelle
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Hauteur</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("position", "y", 0.1)}>↑</button>
                        <button type="button" onClick={() => adjustMoveDraft("position", "y", -0.1)}>↓</button>
                      </div>
                    </div>
                    {moveDraft?.freeMove && (
                      <div className="gallerie-move-group">
                        <span>Gauche/Droite</span>
                        <div className="gallerie-move-buttons">
                          <button type="button" onClick={() => adjustMoveDraft("position", "x", -0.2)}>←</button>
                          <button type="button" onClick={() => adjustMoveDraft("position", "x", 0.2)}>→</button>
                        </div>
                      </div>
                    )}
                    <div className="gallerie-move-group">
                      <span>Decalage</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("position", "z", 0.2)}>←</button>
                        <button type="button" onClick={() => adjustMoveDraft("position", "z", -0.2)}>→</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Taille</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("size", "uniform", 0.1)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("size", "uniform", -0.1)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Hauteur cadre</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("frame_height", "value", 0.1)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("frame_height", "value", -0.1)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Bordure</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("frame_border", "value", 0.05)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("frame_border", "value", -0.05)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Marge v</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("image_margin_v", "value", 0.05)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("image_margin_v", "value", -0.05)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Marge h</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("image_margin_h", "value", 0.05)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("image_margin_h", "value", -0.05)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Cadre</span>
                      <input
                        type="color"
                        value={moveDraft?.frame_color || "#b48840"}
                        onChange={(event) => adjustMoveDraft("frame_color", "value", event.target.value)}
                      />
                    </div>
                    <div className="gallerie-move-group">
                      <span>Lumiere</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("brightness", "value", 0.1)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("brightness", "value", -0.1)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Rotation X</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "x", 0.08)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "x", -0.08)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Rotation Y</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "y", 0.08)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "y", -0.08)}>-</button>
                      </div>
                    </div>
                    <div className="gallerie-move-group">
                      <span>Rotation Z</span>
                      <div className="gallerie-move-buttons">
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "z", 0.08)}>+</button>
                        <button type="button" onClick={() => adjustMoveDraft("rotation", "z", -0.08)}>-</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="gallerie-joysticks">
                <div
                  className="gallerie-joystick"
                  onPointerDown={handleMoveStart}
                  onPointerMove={handleMove}
                  onPointerUp={handleMoveEnd}
                  onPointerCancel={handleMoveEnd}
                >
                  <span className="gallerie-joystick-label">Deplacer</span>
                  <div className="gallerie-joystick-stick" />
                </div>
                <div
                  className="gallerie-joystick"
                  onPointerDown={handleLookStart}
                  onPointerMove={handleLook}
                  onPointerUp={handleLookEnd}
                  onPointerCancel={handleLookEnd}
                >
                  <span className="gallerie-joystick-label">Regarder</span>
                  <div className="gallerie-joystick-stick" />
                </div>
              </div>
            </>
          ) : (
            <div className="gallerie-manage">
              <div className="gallerie-manage-header">
                <div>
                  <h1>Galerie</h1>
                  <p>Aucun layout selectionne. Choisissez-en un pour lancer la visite.</p>
                </div>
                <div className="gallerie-manage-actions">
                  <button type="button" onClick={() => setViewMode("manage")}>
                    Retour
                  </button>
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="gallerie-manage">
            <div className="gallerie-manage-header">
              <div>
                <h1 className="page-title-accent">Galerie</h1>
                <p>Créez vos layouts et placez vos artworks avant d&apos;ouvrir la visite.</p>
              </div>
              <div className="gallerie-manage-actions">
                {selectedLayoutId && (
                  <button type="button" onClick={() => setViewMode("scene")}>
                    Voir la galerie
                  </button>
                )}
              </div>
            </div>
            <div className="gallerie-panels">
              <section className="gallerie-panel">
                <h2>Layouts</h2>
                <form className="gallerie-form" onSubmit={handleCreateLayout}>
                  <input
                    type="text"
                    value={layoutDraft.title}
                    onChange={(event) => setLayoutDraft({ ...layoutDraft, title: event.target.value })}
                    placeholder="Nouveau layout"
                  />
                  <input
                    type="text"
                    value={layoutDraft.description}
                    onChange={(event) => setLayoutDraft({ ...layoutDraft, description: event.target.value })}
                    placeholder="Description (optionnel)"
                  />
                  <button type="submit">Ajouter</button>
                </form>
                <div className="gallerie-layout-list">
                  {layouts.map((layout) => (
                    <div
                      key={layout.layout_id}
                      className={`gallerie-layout-card${layout.layout_id === selectedLayoutId ? " is-active" : ""}`}
                    >
                      <div className="gallerie-layout-title">{layout.title || "Layout"}</div>
                      <div className="gallerie-layout-meta">Statut: {layout.status || "draft"}</div>
                      <div className="gallerie-layout-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLayoutId(layout.layout_id);
                            setSelectedArtworkId("");
                            setArtworkDraft(null);
                          }}
                        >
                          Selectionner
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLayoutId(layout.layout_id);
                            setViewMode("scene");
                          }}
                        >
                          Visiter
                        </button>
                        <select
                          value={layout.status || "draft"}
                          onChange={(event) => handleLayoutStatus(layout.layout_id, event.target.value)}
                        >
                          <option value="draft">Brouillon</option>
                          <option value="published">Publie</option>
                          <option value="archived">Archive</option>
                          <option value="deleted">Supprime</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  {!layouts.length && <div className="gallerie-empty">Aucun layout pour le moment.</div>}
                </div>
                {selectedLayout && (
                  <div className="gallerie-edit">
                    <h3>Modifier le layout</h3>
                    <input
                      type="text"
                      value={layoutEdit.title}
                      onChange={(event) => setLayoutEdit({ ...layoutEdit, title: event.target.value })}
                      placeholder="Titre"
                    />
                    <textarea
                      value={layoutEdit.description}
                      onChange={(event) => setLayoutEdit({ ...layoutEdit, description: event.target.value })}
                      placeholder="Description"
                    />
                    <button type="button" onClick={handleUpdateLayout}>
                      Enregistrer
                    </button>
                  </div>
                )}
              </section>
              <section className="gallerie-panel">
                <h2>Artworks</h2>
                {!selectedLayoutId && <div className="gallerie-empty">Selectionnez un layout.</div>}
                {selectedLayoutId && (
                  <>
                    <div className="gallerie-toolbar">
                      <button type="button" onClick={handleAddArtwork}>
                        Ajouter un artwork
                      </button>
                    </div>
                    {loadingArtworks && <div className="gallerie-empty">Chargement...</div>}
                    <div className="gallerie-artworks-grid">
                      {artworks.map((item) => {
                        const artwork = item.artwork || {};
                        const binding = item.binding || {};
                        const thumb = binding.asset_thumbnail_url || binding.asset_url || "";
                        return (
                          <div
                            key={artwork.artwork_id}
                            className={`gallerie-artwork-card${artwork.artwork_id === selectedArtworkId ? " is-active" : ""}`}
                          >
                            <div className="gallerie-artwork-thumb">
                              {thumb ? <img src={thumb} alt="media" /> : <span>Sans media</span>}
                            </div>
                            <div className="gallerie-artwork-info">
                              <div className="gallerie-artwork-title">{artwork.label || "Artwork"}</div>
                              <div className="gallerie-artwork-meta">{artwork.kind || "painting"}</div>
                            </div>
                            <div className="gallerie-artwork-actions">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedArtworkId(artwork.artwork_id);
                                  setArtworkDraft({
                                    label: artwork.label || "",
                                    kind: artwork.kind || "painting",
                                    position: { ...(artwork.position || { x: 0, y: 0, z: 0 }) },
                                    rotation: { ...(artwork.rotation || { x: 0, y: 0, z: 0 }) },
                                    size: { ...(artwork.size || { w: 1, h: 1, d: 0.1 }) },
                                    wall_side: artwork.wall_side || "",
                                    brightness: typeof artwork.brightness === "number" ? artwork.brightness : 1,
                                    frame_border: typeof artwork.frame_border === "number" ? artwork.frame_border : DEFAULT_FRAME_BORDER,
                                    frame_color: artwork.frame_color || "#b48840",
                                    image_margin_h: typeof artwork.image_margin_h === "number" ? artwork.image_margin_h : DEFAULT_IMAGE_MARGIN_H,
                                    image_margin_v: typeof artwork.image_margin_v === "number" ? artwork.image_margin_v : DEFAULT_IMAGE_MARGIN_V
                                  });
                                }}
                              >
                                Editer
                              </button>
                              <button type="button" onClick={() => openArtworkPicker(artwork.artwork_id)}>
                                Media
                              </button>
                              <button type="button" onClick={() => handleDeleteArtwork(artwork.artwork_id)}>
                                Supprimer
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {!loadingArtworks && artworks.length === 0 && (
                      <div className="gallerie-empty">Aucun artwork pour ce layout.</div>
                    )}
                    {selectedArtworkId && artworkDraft && (
                      <div className="gallerie-edit">
                        <h3>Modifier l&apos;artwork</h3>
                        <input
                          type="text"
                          value={artworkDraft.label}
                          onChange={(event) => setArtworkDraft({ ...artworkDraft, label: event.target.value })}
                          placeholder="Label"
                        />
                        <div className="gallerie-form-row">
                          <input
                            type="text"
                            value={artworkDraft.kind}
                            onChange={(event) => setArtworkDraft({ ...artworkDraft, kind: event.target.value })}
                            placeholder="Type"
                          />
                          <input
                            type="text"
                            value={artworkDraft.wall_side || ""}
                            onChange={(event) => setArtworkDraft({ ...artworkDraft, wall_side: event.target.value })}
                            placeholder="Wall side"
                          />
                        </div>
                        <div className="gallerie-form-row">
                          <input
                            type="number"
                            value={artworkDraft.position?.x ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                position: { ...artworkDraft.position, x: Number(event.target.value) }
                              })
                            }
                            placeholder="Pos X"
                          />
                          <input
                            type="number"
                            value={artworkDraft.position?.y ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                position: { ...artworkDraft.position, y: Number(event.target.value) }
                              })
                            }
                            placeholder="Pos Y"
                          />
                          <input
                            type="number"
                            value={artworkDraft.position?.z ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                position: { ...artworkDraft.position, z: Number(event.target.value) }
                              })
                            }
                            placeholder="Pos Z"
                          />
                        </div>
                        <div className="gallerie-form-row">
                          <input
                            type="number"
                            value={artworkDraft.rotation?.x ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                rotation: { ...artworkDraft.rotation, x: Number(event.target.value) }
                              })
                            }
                            placeholder="Rot X"
                          />
                          <input
                            type="number"
                            value={artworkDraft.rotation?.y ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                rotation: { ...artworkDraft.rotation, y: Number(event.target.value) }
                              })
                            }
                            placeholder="Rot Y"
                          />
                          <input
                            type="number"
                            value={artworkDraft.rotation?.z ?? 0}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                rotation: { ...artworkDraft.rotation, z: Number(event.target.value) }
                              })
                            }
                            placeholder="Rot Z"
                          />
                        </div>
                        <div className="gallerie-form-row">
                          <input
                            type="number"
                            value={artworkDraft.size?.w ?? 1}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                size: { ...artworkDraft.size, w: Number(event.target.value) }
                              })
                            }
                            placeholder="Largeur"
                          />
                          <input
                            type="number"
                            value={artworkDraft.size?.h ?? 1}
                            step="0.1"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                size: { ...artworkDraft.size, h: Number(event.target.value) }
                              })
                            }
                            placeholder="Hauteur"
                          />
                          <input
                            type="number"
                            value={artworkDraft.size?.d ?? 0.1}
                            step="0.05"
                            onChange={(event) =>
                              setArtworkDraft({
                                ...artworkDraft,
                                size: { ...artworkDraft.size, d: Number(event.target.value) }
                              })
                            }
                            placeholder="Profondeur"
                          />
                        </div>
                        <button type="button" onClick={handleSaveArtwork}>
                          Enregistrer
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
